import {
  createContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import {
  signIn,
  signOut as amplifySignOut,
  getCurrentUser,
  fetchUserAttributes,
  type SignInInput,
} from 'aws-amplify/auth'
import { Amplify } from 'aws-amplify'
import { amplifyConfig } from '@/config/amplify.config'
import { parseIndustry, type Industry } from '@/types/profile'

// Initialize Amplify
Amplify.configure(amplifyConfig)

export interface CustomerUser {
  id: string
  customerId: string // Internal customer ID (e.g., cust_abc123)
  accessCode: string // Simple login code for customer app (Cognito username)
  consultantId?: string // Associated consultant
  consultantName?: string // Consultant's display name for customer portal
  consultantWebsite?: string // Consultant's website URL for customer portal
  consultantIndustry?: Industry // Consultant's industry vertical for i18n overlays
  dekId?: string // DEK ID for customer data encryption
  // Note: Email is NOT stored in Cognito for zero-knowledge
  // Email only exists in encrypted DynamoDB records
}

/**
 * Access Code Format
 *
 * The accessCode is a simple, memorable identifier for customer login.
 * Format: 8-character alphanumeric (uppercase letters + digits, no ambiguous chars)
 * Example: "MK7X9P2Q"
 *
 * Generated by backend during invitation creation.
 * Excluded characters: 0, O, 1, I, L (to avoid confusion)
 */

interface AuthContextType {
  user: CustomerUser | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  signIn: (accessCode: string, passphrase: string) => Promise<void>
  // Note: signUp is handled by invitation.service.ts, not directly via Cognito
  // The backend creates the Cognito user during registration
  signOut: () => Promise<void>
  clearError: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<CustomerUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Check for existing session on mount
  useEffect(() => {
    const checkAuthSession = async () => {
      try {
        const currentUser = await getCurrentUser()
        const attributes = await fetchUserAttributes()

        // Zero-knowledge: Cognito username IS the accessCode (simple login code)
        // customerId is stored as custom attribute, email NOT stored in Cognito
        setUser({
          id: currentUser.userId,
          accessCode: currentUser.username, // Cognito username = Access Code
          customerId: attributes['custom:customer_id'] || currentUser.username,
          consultantId: attributes['custom:consultant_id'],
          consultantName: attributes['custom:consultant_name'],
          consultantWebsite: attributes['custom:consultant_website'],
          consultantIndustry: parseIndustry(attributes['custom:consultant_industry']),
          dekId: attributes['custom:dek_id'],
        })
      } catch {
        // No authenticated user
        setUser(null)
      } finally {
        setIsLoading(false)
      }
    }

    checkAuthSession()
  }, [])

  const handleSignIn = async (accessCode: string, passphrase: string) => {
    setIsLoading(true)
    setError(null)
    try {
      // Clear any existing session before signing in
      // This prevents "There is already a signed in user" error from Amplify v6
      try {
        await amplifySignOut()
      } catch {
        // Ignore errors - there may not be a session to clear
      }

      // Zero-knowledge: accessCode is the Cognito username (no email in Cognito)
      const input: SignInInput = { username: accessCode, password: passphrase }
      await signIn(input)

      const currentUser = await getCurrentUser()
      const attributes = await fetchUserAttributes()

      // Zero-knowledge: No email/PII stored in Cognito
      // accessCode = Cognito username, customerId = internal DB reference
      setUser({
        id: currentUser.userId,
        accessCode: currentUser.username,
        customerId: attributes['custom:customer_id'] || currentUser.username,
        consultantId: attributes['custom:consultant_id'],
        consultantName: attributes['custom:consultant_name'],
        consultantWebsite: attributes['custom:consultant_website'],
        consultantIndustry: parseIndustry(attributes['custom:consultant_industry']),
        dekId: attributes['custom:dek_id'],
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign in failed'
      setError(message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const handleSignOut = async () => {
    setIsLoading(true)
    setError(null)
    try {
      await amplifySignOut()
      setUser(null)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign out failed'
      setError(message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const clearError = () => setError(null)

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        error,
        signIn: handleSignIn,
        signOut: handleSignOut,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContext
